import { useList, useGo, useCustomMutation, useDelete, useApiUrl, useGetIdentity } from "@refinedev/core";
import {
  Eye,
  MessageSquare,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  Trash2,
} from "lucide-react";
import { notify } from "@/lib/notify";
import { useConfirm } from "@/hooks/use-confirm";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import type { SupportTicket, TicketStatus } from "./types";

const getStatusBadge = (status: TicketStatus) => {
  switch (status) {
    case "open":
      return (
        <Badge className="bg-red-500/10 text-red-500">
          <AlertCircle className="h-3 w-3 mr-1" />
          Открыт
        </Badge>
      );
    case "in_progress":
      return (
        <Badge className="bg-yellow-500/10 text-yellow-500">
          <Clock className="h-3 w-3 mr-1" />В работе
        </Badge>
      );
    case "pending_user":
      return (
        <Badge className="bg-blue-500/10 text-blue-500">
          <Clock className="h-3 w-3 mr-1" />Ждёт пользователя
        </Badge>
      );
    case "resolved":
      return (
        <Badge className="bg-green-500/10 text-green-500">
          <CheckCircle className="h-3 w-3 mr-1" />
          Решён
        </Badge>
      );
    case "closed":
      return <Badge variant="secondary">Закрыт</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

/**
 * Support tickets list page
 */
export function SupportTicketsList() {
  const go = useGo();
  const apiUrl = useApiUrl();
  const { data: identity } = useGetIdentity<{ id: number }>();
  const { mutate: customMutate } = useCustomMutation();
  const { mutate: deleteOne } = useDelete();
  const { confirm, ConfirmationDialog } = useConfirm();
  useScrollRestoration();
  const [page, setPage] = usePersistedState("support-tickets:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("support-tickets:sort", { field: "createdAt", order: "desc" });
  const [statusFilter, setStatusFilter] = usePersistedState<string>("support-tickets:statusFilter", "all");
  const [search, setSearch] = usePersistedState("support-tickets:search", "");
  const pageSize = 15;

  const filters = [
    ...(statusFilter !== "all"
      ? [{ field: "status", operator: "eq" as const, value: statusFilter }]
      : []),
    ...(search
      ? [{ field: "search", operator: "contains" as const, value: search }]
      : []),
  ];

  const listResult = useList<SupportTicket>({
    resource: "support-tickets",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result?.data ?? [];
  const total = listResult.result?.total ?? 0;
  const isLoading = listResult.query?.isLoading ?? false;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns: Column<SupportTicket>[] = [
    {
      key: "id",
      header: "#",
      width: "60px",
      sortable: true,
    },
    {
      key: "question",
      header: "Вопрос",
      sortable: true,
      render: (_, ticket) => (
        <div className="flex items-center gap-2 max-w-md">
          <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium truncate">{ticket.question}</span>
        </div>
      ),
    },
    {
      key: "user.firstName",
      header: "Пользователь",
      render: (_, ticket) => {
        const name = [ticket.user?.firstName, ticket.user?.lastName]
          .filter(Boolean)
          .join(" ");
        return (
          <div>
            <div className="font-medium">{name || "—"}</div>
            {ticket.user?.username && (
              <div className="text-xs text-muted-foreground">
                @{ticket.user.username}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Статус",
      sortable: true,
      render: (value) => getStatusBadge(value as TicketStatus),
    },
    {
      key: "createdAt",
      header: "Создан",
      sortable: true,
      render: (value) => formatDate(String(value)),
    },
  ];

  const handleClose = (items: SupportTicket[]) => {
    if (items.length === 0) { return; }
    Promise.all(
      items.map(
        (ticket) =>
          new Promise<void>((resolve, reject) =>
            customMutate(
              { url: `${apiUrl}/support-tickets/${ticket.id}`, method: 'patch', values: { status: 'closed' } },
              { onSuccess: () => resolve(), onError: (e) => reject(e) },
            ),
          ),
      ),
    )
      .then(() => {
        notify.success(`Закрыто обращений: ${items.length}`);
        void listResult.query?.refetch();
      })
      .catch(() => notify.error('Не удалось закрыть некоторые обращения'));
  };

  const handleAssignToMe = (items: SupportTicket[]) => {
    if (items.length === 0 || !identity?.id) { return; }
    Promise.all(
      items.map(
        (ticket) =>
          new Promise<void>((resolve, reject) =>
            customMutate(
              { url: `${apiUrl}/support-tickets/${ticket.id}`, method: 'patch', values: { assignedAdminId: identity.id } },
              { onSuccess: () => resolve(), onError: (e) => reject(e) },
            ),
          ),
      ),
    )
      .then(() => {
        notify.success(`Назначено на вас: ${items.length}`);
        void listResult.query?.refetch();
      })
      .catch(() => notify.error('Не удалось назначить некоторые обращения'));
  };

  const handleDelete = async (items: SupportTicket[]) => {
    if (items.length === 0) { return; }
    const confirmed = await confirm({
      title: items.length === 1
        ? `Удалить обращение #${items[0].id}?`
        : `Удалить ${items.length} обращений?`,
      description: 'Это действие необратимо.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }

    for (const ticket of items) {
      deleteOne(
        { resource: 'support-tickets', id: ticket.id },
        {
          onSuccess: () => {
            void listResult.query?.refetch();
          },
          onError: (error) => {
            notify.error(error instanceof Error ? error.message : 'Не удалось удалить обращение');
          },
        },
      );
    }
  };

  const bulkActions: BulkAction<SupportTicket>[] = [
    {
      label: "Просмотр",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/support-tickets/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Закрыть",
      icon: <XCircle className="h-4 w-4" />,
      onClick: handleClose,
      showOnlyWhenSelected: true,
    },
    {
      label: "Назначить на меня",
      icon: <UserCheck className="h-4 w-4" />,
      onClick: handleAssignToMe,
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (items) => void handleDelete(items),
      showOnlyWhenSelected: true,
      variant: 'destructive',
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Обращения"
        description="Поддержка пользователей"
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по вопросу..."
        filters={
          <div className="grid gap-2">
            <Label htmlFor="ticket-status">Статус</Label>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger id="ticket-status">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="OPEN">Открытые</SelectItem>
                <SelectItem value="IN_PROGRESS">В работе</SelectItem>
                <SelectItem value="PENDING_USER">Ждут пользователя</SelectItem>
                <SelectItem value="RESOLVED">Решённые</SelectItem>
                <SelectItem value="CLOSED">Закрытые</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        filtersCount={statusFilter === "all" ? 0 : 1}
        onResetFilters={() => {
          setStatusFilter("all");
          setSearch("");
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(ticket) =>
          go({ to: `/support-tickets/show/${ticket.id}` })
        }
        emptyMessage="Нет обращений"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />
      {ConfirmationDialog}
    </div>
  );
}
